/**
 * Payment-policy facts shared by the customer UI, the Express server, and
 * Tommy's system prompt. This module is the single source of truth for the
 * numbers and rules customers are told — when a fee or cadence changes,
 * change it HERE and every surface (UI copy, Tommy's knowledge, tests)
 * follows automatically.
 *
 * Must stay dependency-free plain JS: it is imported by Vite (frontend),
 * Node (server.js / tommy-concierge.js), and vitest.
 *
 * The Supabase Edge Functions run on Deno and cannot import this module;
 * `server/tommy-drift.test.js` asserts their copies of these constants
 * stay equal so they cannot silently diverge.
 */

/** One-time fee to skip the next installment (Defera Capital Stripe rail). */
export const SKIP_PAYMENT_FEE_CENTS = 2000;

/** One-time fee for a final-payment-date extension (legacy Slice Pay rail). */
export const EXTENSION_FEE_CENTS = 3500;

/**
 * One-time fee to reinstate a defaulted trip, charged upfront to the card on
 * file when the admin executes the reinstatement (both Stripe and NMI
 * tracks). The customer must accept this fee in chat before the request is
 * filed — see the Defaulted Trips flow in tommy-concierge.js. The Deno copy
 * lives in admin-reinstate-defaulted-trip/index.ts (drift-tested).
 */
export const REINSTATEMENT_FEE_CENTS = 3000;

/** Customer-facing "pick your own payment dates" inside Change Schedule.
 *  Flat fee, charged to the card on file at confirm; the new schedule must
 *  finish at least SCHEDULE_DATE_CHANGE_CUTOFF_DAYS before departure. */
export const SCHEDULE_DATE_CHANGE_FEE_CENTS = 2500;
export const SCHEDULE_DATE_CHANGE_CUTOFF_DAYS = 5;

/**
 * Move a payment ("Can't make this payment?", founder 2026-10-25): up to
 * PAYMENT_EXTENSION_MAX_DAYS later, for a fee by how far it moves, spread
 * across the remaining payments (nothing charged that day). Once per payment,
 * PAYMENT_EXTENSIONS_PER_TRIP per trip, never refunded on a cancellation.
 * The Deno copy is supabase/functions/_shared/payment-extension.ts
 * (drift-tested).
 */
export const PAYMENT_EXTENSION_FEE_TIERS = [
  { maxDays: 7, feeCents: 3500 },
  { maxDays: 14, feeCents: 5000 },
];
export const PAYMENT_EXTENSION_MAX_DAYS = 14;
export const PAYMENT_EXTENSIONS_PER_TRIP = 2;

/** The only installment cadences that exist. There is no monthly. */
export const PAYMENT_FREQUENCIES = ['weekly', 'biweekly'];
export const PAYMENT_FREQUENCY_LABELS = { weekly: 'Weekly', biweekly: 'Bi-weekly' };

/**
 * Request Extension bounds (legacy Slice Pay): how far the FINAL payment
 * date can move, stepped by the plan's own cadence and capped before the
 * travel cutoff. Mirrors the option builder in PaymentScheduleDisplay.
 */
export const EXTENSION_WEEKS = {
  weekly: { min: 1, max: 4 },
  biweekly: { min: 2, max: 8 },
};

/**
 * Payment-method codes that put a booking on the Stripe rail (inline
 * Make a Payment / Change Schedule, and Skip Payment for Capital).
 * Everything else is the legacy Slice Pay portal. Mirrors
 * FlightDetailsModal's `isDeferaZero` / `isDeferaCapital` checks — plain
 * `installments` deliberately stays legacy.
 */
export const DEFERA_ZERO_METHODS = new Set(['defera_zero', 'deferazero']);
export const DEFERA_CAPITAL_METHODS = new Set(['defera_capital', 'deferacapital']);

export function paymentRailForMethod(rawMethod) {
  const m = String(rawMethod || '').toLowerCase().trim();
  if (DEFERA_ZERO_METHODS.has(m)) return 'stripe_zero';
  if (DEFERA_CAPITAL_METHODS.has(m)) return 'stripe_capital';
  if (m === 'miles' || m === 'miles_only') return 'miles_only';
  if (m === 'reserve') return 'reserve';
  return 'slice_pay';
}

export function formatCentsAsDollars(cents) {
  const n = Number(cents) || 0;
  // Whole dollars stay bare ("$25", not "$25.00") — that shorthand is what the
  // fee labels below read like in a sentence. Anything else gets grouped, so a
  // four-figure amount doesn't render as "$1234.50".
  // Inlined rather than importing @/utils/money: this module is deliberately
  // dependency-free so Node and Deno can import it without the Vite alias.
  return `$${(n / 100).toLocaleString('en-US', {
    minimumFractionDigits: n % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

export const SKIP_PAYMENT_FEE_LABEL = formatCentsAsDollars(SKIP_PAYMENT_FEE_CENTS);
export const EXTENSION_FEE_LABEL = formatCentsAsDollars(EXTENSION_FEE_CENTS);
/** The move-a-payment Extension Fee in a sentence: "$35 for up to 7 days later, $50 for up to 14". */
export const PAYMENT_EXTENSION_FEE_SUMMARY = PAYMENT_EXTENSION_FEE_TIERS
  .map((t, i) => `${formatCentsAsDollars(t.feeCents)} for up to ${t.maxDays}${i === 0 ? ' days later' : ''}`)
  .join(', ');
export const REINSTATEMENT_FEE_LABEL = formatCentsAsDollars(REINSTATEMENT_FEE_CENTS);
export const SCHEDULE_DATE_CHANGE_FEE_LABEL = formatCentsAsDollars(SCHEDULE_DATE_CHANGE_FEE_CENTS);
